import { useCallback, useEffect, useState, useMemo } from 'react';
import websocketService from '../services/websocketService';

/**
 * Custom hook for WebSocket market data streaming using singleton service
 * Provides auto-reconnect and shared connection across all components
 *
 * @param {string} stream - Stream name (positions, pnl, option-chain, indices)
 * @param {object} params - Optional parameters for the stream
 * @param {boolean} enabled - Whether to connect and subscribe (default: true)
 * @returns {object} { data, isConnected }
 */
export const useMarketData = (stream, params = {}, enabled = true, shouldAcceptMessage = null) => {
  const [data, setData] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  // Stringify params to create stable dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paramsKey = useMemo(() => JSON.stringify(params), [JSON.stringify(params)]);

  // Connect to WebSocket once on first mount
  useEffect(() => {
    websocketService.connect();
  }, []); // Empty deps - connect only once

  // Track connection status
  useEffect(() => {
    const handleStatusChange = (status) => {
      setIsConnected(status === 'Connected');
    };

    // Register status listener
    websocketService.addStatusChangeListener(handleStatusChange);

    // Get initial status
    const initialStatus = websocketService.getConnectionStatus();
    setIsConnected(initialStatus === 'Connected');

    // Cleanup
    return () => {
      websocketService.removeStatusChangeListener(handleStatusChange);
    };
  }, []);

  // Subscribe to stream
  useEffect(() => {
    if (!enabled) return;

    // Message handler for this stream
    const handleMessage = (messageData) => {
      if (shouldAcceptMessage && !shouldAcceptMessage(messageData)) {
        return;
      }
      setData(messageData);
    };

    // Add handler and subscribe
    websocketService.addMessageHandler(stream, handleMessage);
    websocketService.subscribe(stream, JSON.parse(paramsKey));

    // Cleanup on unmount or dependency change
    return () => {
      websocketService.removeMessageHandler(stream, handleMessage);
      // Note: We always unsubscribe on cleanup. The websocketService.subscribe()
      // call on re-render will immediately re-subscribe before any data is missed.
      websocketService.unsubscribe(stream);
    };
  }, [enabled, stream, paramsKey, shouldAcceptMessage]);

  return {
    data,
    isConnected,
  };
};

/**
 * Hook for positions stream
 */
export const usePositionsData = (enabled = true) => {
  return useMarketData('positions', {}, enabled);
};

/**
 * Hook for PnL stream
 */
export const usePnLData = (enabled = true) => {
  return useMarketData('pnl', {}, enabled);
};

/**
 * Hook for indices stream
 */
export const useIndicesData = (enabled = true) => {
  return useMarketData('indices', {}, enabled);
};

/**
 * Hook for option chain stream
 */
export const useOptionChainData = (index, expiry, strikes, enabled = true) => {
  const normalizedStrikes = useMemo(() => {
    const value = Number.parseInt(strikes, 10);
    return Number.isFinite(value) && value > 0 ? value : 20;
  }, [strikes]);
  const params = useMemo(
    () => ({ index, expiry, strikes: normalizedStrikes }),
    [index, expiry, normalizedStrikes],
  );
  const shouldAcceptOptionChainMessage = useCallback((messageData) => {
    const payload = messageData?.success && messageData?.data
      ? messageData.data
      : messageData;
    const responseStrikes = Number.parseInt(payload?.requested_strikes, 10);

    return (
      payload?.index === index &&
      payload?.expiry === expiry &&
      Number.isFinite(responseStrikes) &&
      responseStrikes === normalizedStrikes
    );
  }, [index, expiry, normalizedStrikes]);

  return useMarketData('option-chain', params, enabled, shouldAcceptOptionChainMessage);
};

/**
 * Hook for derived data stream (Index Price, Synthetic Future, Straddle Price)
 * Filters by index name since backend broadcasts all indices
 */
export const useDerivedData = (indexName, enabled = true) => {
  const [data, setData] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    websocketService.connect();
    setIsConnected(websocketService.getConnectionStatus() === 'Connected');
  }, []);

  // Effect 1: Manage message handler (recreated when indexName changes for filtering)
  useEffect(() => {
    if (!enabled || !indexName) return;

    const handleMessage = (messageData) => {
      // Filter by index name (since stream broadcasts all indices)
      if (messageData.index_name === indexName) {
        setData(messageData);
      }
    };

    websocketService.addMessageHandler('derived-data', handleMessage);

    return () => {
      websocketService.removeMessageHandler('derived-data', handleMessage);
    };
  }, [enabled, indexName]);

  // Effect 2: Manage subscription (only unsubscribe on unmount)
  useEffect(() => {
    if (!enabled) return;

    websocketService.subscribe('derived-data', {});

    return () => {
      websocketService.unsubscribe('derived-data');
    };
  }, [enabled]);

  return { data, isConnected };
};
